// AutocompletePlaces.js

import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTrackEvent } from 'util/trackEvent';
import { debounce, throttle } from "lodash";
import { anonForwardGeocode, anonReverseGeocode } from 'services/anon.service';
import locationsService from 'services/locations.service';
import { toast } from "react-hot-toast";
import { CircularProgress } from "@material-ui/core";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { useUpdateEffect } from "usehooks-ts";
import { reverse } from "voca";
import ReactJson from "react-json-view";
import { Typography } from "@material-ui/core";
import bdistance from '@turf/distance'
import { localizeNumber } from 'util/localizeFormat';
import { toasterror } from 'util/toasterror';

const AltLocationAutocomplete = ({
  browserLocation = null,
  sessionToken = null,
  centerLocation = {
    latitude: 14.562909599624389,
    longitude: 121.03317570610837
  },
  onSelectAddress = (address) => { },
  overrideAddress = null
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const [geoCodedSelection, setGeoCodedSelection] = useState(null);
  const trackEvent = useTrackEvent()
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null)

  const [fromPrefill, setFromPrefill] = useState(false);

  const reverseGeo = useCallback(
    debounce(async (value) => {
      try {
        setLoading(true)
        let geoResult = null
        if (sessionToken) {
          geoResult = await anonReverseGeocode({
            latitude: value?.latitude,
            longitude: value?.longitude,
            session_token: sessionToken
          })
        } else {
          geoResult = await locationsService.reverseGeocode({ latitude: value?.latitude, longitude: value?.longitude })
        }
        const geocode = {
          latitude: value?.latitude,
          longitude: value?.longitude,
          ...geoResult,
          display_address: geoResult?.address,
        }
        setSelectedOption(geocode)
        setGeoCodedSelection(geocode)
        onSelectAddress(geocode)
        setFromPrefill(true)
        setInputValue(geocode?.display_address)
      } catch (error) {
        toasterror(error?.message?.en || error?.response?.data?.message?.en || error?.response?.data?.details)
      } finally {
        setLoading(false)
      }
    }, 1000), [sessionToken, onSelectAddress]
  );

  useUpdateEffect(() => {
    if (overrideAddress?.latitude && overrideAddress?.longitude) {
      reverseGeo(overrideAddress)
    }

  }, [overrideAddress?.latitude, overrideAddress?.longitude])


  const fetchAutoComplete = useCallback(
    async (params) => {
      setLoading(true)
      try {
        let result = null
        if (sessionToken) {
          result = await anonForwardGeocode(params?.query, params?.latitude, params?.longitude, sessionToken)
        } else {
          result = await locationsService.forwardGeocode(params?.query, params?.latitude, params?.longitude)
        }
        setOptions(result?.list || []);
      } catch (error) {
        toasterror(error?.message?.en || error?.response?.data?.message?.en || error?.response?.data?.details)
        setOptions([])
      } finally {
        setLoading(false)
      }

      return null
    },
    [inputValue, sessionToken]
  );

  const handleFocus = () => {
    const inputTopPosition = inputRef.current.getBoundingClientRect().top;
    window.scrollTo({
      top: inputTopPosition,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    // Adjust scroll position when the keyboard is shown on mobile devices
    const handleResize = () => {
      if (document.activeElement === inputRef.current) {
        handleFocus();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const debouncedEventHandler = useCallback(debounce((val, loc) => {
    const params = {
      query: val,
      latitude: loc?.latitude,
      longitude: loc?.longitude
    }
    fetchAutoComplete(params)
  }, 500), []);


  useUpdateEffect(() => {
    debouncedEventHandler(inputValue, centerLocation)
  }, [inputValue, centerLocation])

  return (
    <>
      <Autocomplete
        freeSolo
        filterOptions={(x) => x}
        options={options?.map((option) => {
          let dist = Number.MAX_SAFE_INTEGER
          if (browserLocation) {
            dist = bdistance([option?.longitude, option?.latitude], [browserLocation?.lng, browserLocation?.lat], { units: 'kilometers' })
          }
          return {
            ...option,
            dist
          }
        })?.sort((a, b) => {
          return a?.dist - b?.dist
        })
        }
        renderOption={(option) => {
          let dist = 'n/a'
          if (browserLocation) {
            dist = `${localizeNumber(bdistance([option?.longitude, option?.latitude], [browserLocation?.lng, browserLocation?.lat], { units: 'kilometers' }), true)} km`
          }

          return (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%'
            }}>


              <div style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%'
              }}>
                <span>
                  {option?.name}
                </span>
                <span>{
                  browserLocation && `${dist} away from your location`
                }
                </span>
              </div>

              <Typography variant='caption' color="textSecondary">{option?.display_address}</Typography>
            </div>
          )
        }
        }
        inputValue={inputValue}
        getOptionSelected={(option, value) => {
          return option?.display_address === value?.display_address
        }}
        getOptionLabel={(option) => option?.display_address || ''}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') {
            trackEvent('Location Autocomplete - Search', { location: newInputValue })
            setInputValue(newInputValue)

            setFromPrefill(false)
          }
          if (reason === 'clear') {
            setSelectedOption(null)
            setInputValue('')
            setGeoCodedSelection(null)
            onSelectAddress(null)
            setOptions([])
          }
        }}
        onChange={(event, newValue, reason) => {
          switch (reason) {
            case 'clear':
              setSelectedOption(null)
              break;
            case 'select-option':
              trackEvent('Location Autocomplete - Select', { location: newValue })
              setSelectedOption(newValue)
              onSelectAddress(newValue)
              reverseGeo(newValue)
              break;
            default:
              break;
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            onFocus={(e) => {
              e.preventDefault()
              e.target.select()
              handleFocus()
            }}

            placeholder="Search places"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {
                    selectedOption?.display_address ?
                      <FontAwesomeIcon icon={faCheckCircle} color="green" /> : <FontAwesomeIcon icon={faCircleXmark} color="red" />
                  }
                  {params.InputProps.endAdornment}
                </>
              ),

            }}
          />
        )}
      />
    </>
  );
};

export default AltLocationAutocomplete;